import React, { useState } from 'react';
import Modal from './Modal';
import { BankAccount, BankAccountTransferRequest } from '../types';

interface TransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    bankAccounts: BankAccount[];
    onSubmit: (data: BankAccountTransferRequest) => void;
    isSubmitting?: boolean;
    submitError?: string | null;
}

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const parseToCents = (raw: string): number => {
    const onlyDigits = raw.replace(/\D/g, '');
    if (!onlyDigits) return 0;
    const cents = parseInt(onlyDigits, 10);
    return cents / 100;
};

const TransferModal: React.FC<TransferModalProps> = ({
    isOpen,
    onClose,
    bankAccounts,
    onSubmit,
    isSubmitting = false,
    submitError = null
}) => {
    const [bankAccountOrigin, setBankAccountOrigin] = useState<string>('');
    const [bankAccountDestination, setBankAccountDestination] = useState<string>('');
    const [amountInput, setAmountInput] = useState<string>('');
    const [description, setDescription] = useState<string>('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const amountCents = Math.round(parseToCents(amountInput) * 100);
        if (!bankAccountOrigin || !bankAccountDestination || amountCents <= 0) return;
        if (bankAccountOrigin === bankAccountDestination) return;
        onSubmit({
            bankAccountOrigin,
            bankAccountDestination,
            amount: amountCents,
            description: description.trim() || ''
        });
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const numberValue = parseToCents(raw);
        setAmountInput(formatBRL(numberValue));
    };

    const destinationOptions = bankAccounts.filter((acc) => acc.id !== bankAccountOrigin);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Transferências entre contas">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="bankAccountOrigin" className="block text-sm font-medium text-secondary-700 mb-1">
                        Conta de origem *
                    </label>
                    <select
                        id="bankAccountOrigin"
                        value={bankAccountOrigin}
                        onChange={(e) => {
                            setBankAccountOrigin(e.target.value);
                            if (e.target.value === bankAccountDestination) setBankAccountDestination('');
                        }}
                        required
                        className="input-field"
                    >
                        <option value="">Selecione a conta de origem</option>
                        {bankAccounts.map((acc) => (
                            <option key={acc.id} value={acc.id}>
                                {acc.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label htmlFor="bankAccountDestination" className="block text-sm font-medium text-secondary-700 mb-1">
                        Conta de destino *
                    </label>
                    <select
                        id="bankAccountDestination"
                        value={bankAccountDestination}
                        onChange={(e) => setBankAccountDestination(e.target.value)}
                        required
                        className="input-field"
                    >
                        <option value="">Selecione a conta de destino</option>
                        {destinationOptions.map((acc) => (
                            <option key={acc.id} value={acc.id}>
                                {acc.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-secondary-700 mb-1">
                        Valor *
                    </label>
                    <input
                        type="text"
                        inputMode="numeric"
                        id="amount"
                        value={amountInput}
                        onChange={handleAmountChange}
                        required
                        className="input-field"
                        placeholder="R$ 0,00"
                    />
                </div>

                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-secondary-700 mb-1">
                        Descrição
                    </label>
                    <input
                        type="text"
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="input-field"
                        placeholder="Ex.: Transferência para reserva"
                    />
                </div>

                {submitError && (
                    <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{submitError}</div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={onClose} className="btn-secondary" disabled={isSubmitting}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={
                            isSubmitting ||
                            !bankAccountOrigin ||
                            !bankAccountDestination ||
                            bankAccountOrigin === bankAccountDestination ||
                            !amountInput ||
                            parseToCents(amountInput) * 100 < 1
                        }
                    >
                        {isSubmitting ? 'Transferindo...' : 'Transferir'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default TransferModal;
